import { memoize } from '@/lib/memoize';
import type { Campus, CampusRoleKey } from '@/shared/types/campus';
import type { Entity } from '@/shared/types/entity';
import type { GroupTypeKey } from '@/shared/types/group';
import type { Ministry, MinistryTypeKey } from '@/shared/types/ministry';

export const AllCampusRoleKeys: CampusRoleKey[] = [
  'oversight',
  'admin',
  'director',
];

export interface State {
  campuses: {
    data: Record<string, Campus>;
    loaded: boolean;
  };
  ministries: {
    data: Ministry[];
    loaded: boolean;
  };
  entities: {
    data: Record<string, Entity>;
    loaded: boolean;
  };
}

export type OrgTypeKey = 'organization' | 'campus' | 'ministry';

export interface OrgTypeEntry {
  alias: {
    type: string;
  };
  roles: {
    key: string;
    label: string;
    plural: string;
  }[];
}

export const OrgProperties: Record<OrgTypeKey, OrgTypeEntry> = {
  organization: {
    alias: {
      type: 'Organization',
    },
    roles: [{ key: 'oversight', label: 'Lead Pastor', plural: 'Lead Pastors' }],
  },
  campus: {
    alias: {
      type: 'Campus',
    },
    roles: [
      {
        key: 'oversight',
        label: 'Campus Pastor',
        plural: 'Campus Pastors',
      },
      {
        key: 'admin',
        label: 'Campus Admin',
        plural: 'Campus Admins',
      },
      {
        key: 'director',
        label: 'Campus Director',
        plural: 'Campus Directors',
      },
    ],
  },
  ministry: {
    alias: {
      type: 'Ministry',
    },
    roles: [
      {
        key: 'oversight',
        label: 'Ministry Pastor',
        plural: 'Ministry Pastors',
      },
      {
        key: 'admin',
        label: 'Ministry Admin',
        plural: 'Ministry Admins',
      },
      {
        key: 'director',
        label: 'Ministry Director',
        plural: 'Ministry Directors',
      },
    ],
  },
};

export function getOrgAlias(
  type: OrgTypeKey,
  key: keyof OrgTypeEntry['alias'],
  plural = false,
) {
  const alias = OrgProperties[type].alias[key];

  return plural ? alias + 's' : alias;
}

export function getOrgRoleLabel(
  type: OrgTypeKey,
  key: CampusRoleKey | MinistryRoleKey,
  plural = false,
) {
  const entry = OrgProperties[type].roles.find((role) => role.key === key);

  return (plural ? entry?.plural : entry?.label) || '';
}

export interface MinistryType {
  key: MinistryTypeKey;
  name: string;
  icon: string;
}

export const MinistryTypes: MinistryType[] = [
  {
    key: 'pastoral_care',
    name: 'Pastoral Care',
    icon: 'mdi:heart-pulse',
  },
  {
    key: 'worship_experience',
    name: 'Worship Experience',
    icon: 'mdi:human-handsup',
  },
  {
    key: 'guest_experience',
    name: 'Guest Experience',
    icon: 'mdi:handshake',
  },
  {
    key: 'youth',
    name: 'Vox Gen',
    icon: 'mdi:human-male-child',
  },
  {
    key: 'kids',
    name: 'Royal Kids',
    icon: 'mdi:human-male-child',
  },
  {
    key: 'mens',
    name: 'Brotherhood',
    icon: 'mdi:human-male',
  },
  {
    key: 'womens',
    name: 'Sisterhood',
    icon: 'mdi:human-female',
  },
  {
    key: 'assimilation',
    name: 'Assimilation',
    icon: 'mdi:map-search',
  },
  {
    key: 'development',
    name: 'Development',
    icon: 'mdi:learn-outline',
  },
  {
    key: 'outreach',
    name: 'Hope Hangar',
    icon: 'mdi:sprout',
  },
  {
    key: 'marketplace',
    name: 'Hype Network',
    icon: 'mdi:sprout',
  },
];

export const MinistryTypeLookup = memoize(MinistryTypes, 'key');

export function getMinistryTypeName(type: MinistryTypeKey) {
  return MinistryTypeLookup[type]?.name;
}

export function getMinistryTypeIcon(type: MinistryTypeKey) {
  return MinistryTypeLookup[type]?.icon;
}

export interface Structure {
  ministry: MinistryTypeKey;
  type: GroupTypeKey;
  label: string;
  plural: string;
  parent: GroupTypeKey | OrgTypeKey;
  roles: MinistryRole[];
}

export type MinistryRoleKey = keyof Ministry['data']['roles'];

export const AllMinistryRoleKeys: MinistryRoleKey[] = [
  'oversight',
  'director',
  'admin',
];

export interface MinistryRole {
  key: MinistryRoleKey;
  label: string;
  plural: string;
}

export const MinistryStructure: { [_K in MinistryTypeKey]?: Structure[] } = {
  pastoral_care: [
    {
      ministry: 'pastoral_care',
      parent: 'ministry',
      type: 'laygroup1',
      label: 'Sheepfold Section',
      plural: 'Sheepfold Sections',
      roles: [
        {
          key: 'oversight',
          label: 'Lay Pastor',
          plural: 'Lay Pastors',
        },
      ],
    },
    {
      ministry: 'pastoral_care',
      parent: 'laygroup1',
      type: 'laygroup2',
      label: 'Sheepfold Group',
      plural: 'Sheepfold Groups',
      roles: [
        {
          key: 'oversight',
          label: 'Lay Leader',
          plural: 'Lay Leaders',
        },
      ],
    },
    {
      ministry: 'pastoral_care',
      parent: 'laygroup1',
      type: 'pcogroup',
      label: 'PCO Group',
      plural: 'PCO Groups',
      roles: [
        {
          key: 'oversight',
          label: 'Group Leader',
          plural: 'Group Leaders',
        },
      ],
    },
  ],
  worship_experience: [
    {
      ministry: 'worship_experience',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  guest_experience: [
    {
      ministry: 'guest_experience',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  youth: [
    {
      ministry: 'youth',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
    {
      ministry: 'youth',
      parent: 'ministry',
      type: 'group',
      label: 'Tribe',
      plural: 'Tribes',
      roles: [],
    },
  ],
  kids: [
    {
      ministry: 'kids',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  mens: [
    {
      ministry: 'mens',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  womens: [
    {
      ministry: 'womens',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  assimilation: [
    {
      ministry: 'assimilation',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  development: [
    {
      ministry: 'development',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  outreach: [
    {
      ministry: 'outreach',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
  marketplace: [
    {
      ministry: 'marketplace',
      parent: 'ministry',
      type: 'team',
      label: 'Team',
      plural: 'Teams',
      roles: [],
    },
  ],
};

export function getMinistryStructure(key: MinistryTypeKey) {
  return MinistryStructure[key];
}
